/**
 * React Query hooks for Document Import.
 *
 * T094 [US5]: Implement useDocumentUpload React Query hook with progress tracking
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import {
  uploadDocument,
  getDocumentStatus,
  getExtractedData,
  saveExtractedData,
  cancelDocumentImport,
  DocumentStatusResponse,
  ExtractedDataResponse,
  SaveExtractedDataRequest,
} from '../documentImportApi';
import { DEBUG } from '../../../config/api';

export interface DocumentUploadParams {
  fileData: string; // Base64-encoded file data
  fileType: 'image' | 'pdf';
  fileName?: string;
}

/**
 * Hook to upload a document for AI processing.
 * Provides upload progress tracking and error handling.
 *
 * T094 [US5]: React Query hook for document upload with base64 support.
 */
export function useDocumentUpload() {
  return useMutation({
    mutationFn: async (params: DocumentUploadParams) => {
      return await uploadDocument(params.fileData, params.fileType, params.fileName);
    },
    onError: (error: Error) => {
      console.error('Document upload failed:', error);
      throw error; // Re-throw to let component handle UI feedback
    },
  });
}

/**
 * Hook to poll document processing status.
 * Automatically refetches every 2 seconds until status is 'complete' or 'failed'.
 *
 * @param importId - Document import ID
 * @param enabled - Whether to enable polling (default: true)
 */
export function useDocumentStatus(importId: number, enabled: boolean = true) {
  return useQuery<DocumentStatusResponse>({
    queryKey: ['document-status', importId],
    queryFn: () => {
      if (DEBUG) console.log('[useDocumentStatus] Fetching status for importId:', importId);
      return getDocumentStatus(importId);
    },
    enabled: enabled && !!importId,
    refetchInterval: 2000, // Always poll every 2 seconds when enabled
    refetchIntervalInBackground: true, // Continue polling when app is in background
    onSuccess: (data) => {
      if (DEBUG)
        console.log('[useDocumentStatus] Status received:', data, '- status:', data.status);
    },
    onError: (error) => {
      console.error('[useDocumentStatus] Error fetching status:', error);
    },
  });
}

/**
 * Hook to get extracted positions and trades from a processed document.
 *
 * @param importId - Document import ID
 * @param enabled - Whether to enable query (default: true)
 */
export function useExtractedData(importId: number, enabled: boolean = true) {
  return useQuery<ExtractedDataResponse>({
    queryKey: ['extracted-data', importId],
    queryFn: () => getExtractedData(importId),
    enabled: enabled && !!importId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Hook to save user-verified extracted data to the database.
 */
export function useSaveExtractedData() {
  return useMutation({
    mutationFn: async ({
      importId,
      data,
    }: {
      importId: number;
      data: SaveExtractedDataRequest;
    }) => {
      return await saveExtractedData(importId, data);
    },
    onError: (error: Error) => {
      console.error('Failed to save extracted data:', error);
      throw error;
    },
  });
}

/**
 * Hook to cancel a document import and discard extracted data.
 */
export function useCancelDocumentImport() {
  return useMutation({
    mutationFn: async (importId: number) => {
      await cancelDocumentImport(importId);
    },
    onError: (error: Error) => {
      console.error('Failed to cancel document import:', error);
      throw error;
    },
  });
}
